import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { ProgressCarousel } from "@src/appV2/redesign/components/ProgressCarousel";
import {
  BottomSheet,
  type UseModalState,
  useModalState,
} from "@src/appV2/redesign/system/ui-components";
import { type InternalTimeClockTutorialStep } from "@src/appV2/Shifts/Shift/ShiftState/types";
import { useState } from "react";

import { InternalTimeClockNotWorkingReasonBottomSheet } from "./InternalTimeClockNotWorkingReasonBottomSheet";
import { InternalTimeClockTutorialSlide } from "./InternalTimeClockTutorialSlide";

const BOTTOM_SHEET_CLOSING_TRANSITION_IN_MS = 300;

interface InternalTimeClockTutorialBottomSheetProps {
  modalState: UseModalState;
  action: "clock-in" | "clock-out";
  variant?: "ukg" | "nfc";
  steps: InternalTimeClockTutorialStep[];
  /**
   * e.g. if placeholders is { workplaceName: "Foo" }, then for tutorial headline and body,
   * if "{workplaceName}" is present, it will be replaced by "Foo"
   */
  placeholders: Record<string, string>;
  onTimeclockActionPerformed: () => void;
  onTimeclockNotWorking: () => void;
}

export function InternalTimeClockTutorialBottomSheet(
  props: InternalTimeClockTutorialBottomSheetProps
) {
  const {
    modalState,
    action,
    variant = "ukg",
    steps,
    placeholders,
    onTimeclockActionPerformed,
    onTimeclockNotWorking,
  } = props;
  const timeClockNotWorkingOptionsModalState = useModalState();
  const [carouselIndex, setCarouselIndex] = useState(0);

  function closeBottomSheet() {
    modalState.closeModal();

    setTimeout(() => {
      setCarouselIndex(0);
    }, BOTTOM_SHEET_CLOSING_TRANSITION_IN_MS);
  }

  return (
    <>
      <BottomSheet
        modalState={modalState}
        paperSx={{ height: "90vh" }}
        onClose={() => {
          closeBottomSheet();
        }}
      >
        <ProgressCarousel currentIndex={carouselIndex} onIndexChange={setCarouselIndex}>
          {steps.map((step, index) => (
            <InternalTimeClockTutorialSlide
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              action={action}
              variant={variant}
              step={step}
              placeholders={placeholders}
              onActionPerformed={onTimeclockActionPerformed}
              onTimeclockNotWorking={() => {
                if (variant === "nfc") {
                  onTimeclockNotWorking();
                } else {
                  timeClockNotWorkingOptionsModalState.openModal();
                }
              }}
              onSkipNavigationSlides={() => {
                setCarouselIndex(
                  steps.findIndex((step) => step.tutorialSection === "timeclock-use")
                );
              }}
              onClose={() => {
                closeBottomSheet();
              }}
            />
          ))}
        </ProgressCarousel>
      </BottomSheet>
      <InternalTimeClockNotWorkingReasonBottomSheet
        modalState={timeClockNotWorkingOptionsModalState}
        onReasonPicked={(reason) => {
          logEvent(APP_V2_APP_EVENTS.TIMEKEEPING_INTEGRATIONS_INTERNAL_TIMECLOCK_NOT_WORKING, {
            reason,
          });
          onTimeclockNotWorking();
          timeClockNotWorkingOptionsModalState.closeModal();
        }}
        onCancel={timeClockNotWorkingOptionsModalState.closeModal}
      />
    </>
  );
}
